import commonGame from '../commonGame';
import common from '../common';
import Pencil from './drawing_tools/Pencils/Pencil';
import Brush from './drawing_tools/Brushes/Brush';
import Spray from './drawing_tools/Spray';
import Stamp from './drawing_tools/Stamp';
import InkPot from './drawing_tools/InkPot';
import Eraser from './drawing_tools/Eraser';

const _onDrawLayer_MouseDown = Symbol('_onDrawLayer_MouseDown');
const _onDrawLayer_MouseMove = Symbol('_onDrawLayer_MouseMove');
const _onDrawLayer_MouseUp = Symbol('_onDrawLayer_MouseUp');
const _onDrawLayer_MouseOut = Symbol('_onDrawLayer_MouseOut');
const _onThickness_Changed = Symbol('_onThickness_Changed');
const _onColour_Changed = Symbol('_onColour_Changed');
const _onDrawMode_Changed = Symbol('_onDrawMode_Changed');

export default class DrawLayer extends PIXI.Container {
	constructor() {
		super();
		// States.
		this.STATE = {
			IDLE: 0,
			DRAWING: 1
		};

		// Attributes.
		this._state = this.STATE.IDLE;

		this._canvas2d = null;
		this._context = null;

		// TEST: DYN_OUT:
		this._dynOutline_c2d = null;
		this._dynOutlineContext = null;
		this._dynOutlineLayerData = null;

		this._pencil = null;
		this._brush = null;
		this._spray = null;
		this._stamp = null;
		this._inkPot = null;
		this._drawingTool = null;
		this._eraser = null;

		this.allStamps = [];

		this._drawPS = null;

		// Sprites.
		this._layerSprite = null;

		// Input.
		this._lastInputLocalPosition = new PIXI.Point();
		this._updateInputLocalPosition = new PIXI.Point();
	}

	init() {
		// Canvas.
		this._canvas2d = document.createElement('canvas');
		this._canvas2d.width = common.game.canvas.WIDTH;
		this._canvas2d.height = common.game.canvas.HEIGHT;

		// Context.
		this._context = this._canvas2d.getContext('2d');

		// TEST: DYN_OUT:
		this._dynOutline_c2d = document.createElement('canvas');
		this._dynOutline_c2d.width = common.game.canvas.WIDTH;
		this._dynOutline_c2d.height = common.game.canvas.HEIGHT;

		this._dynOutlineContext = this._dynOutline_c2d.getContext('2d');
		this._dynOutlineContext.fillStyle = 'rgba(0, 0, 0, 0)';
		this._dynOutlineContext.fillRect(0, 0, this._dynOutline_c2d.width, this._dynOutline_c2d.height);

		this._dynOutlineLayerData = this._dynOutlineContext.getImageData(0, 0, this._dynOutline_c2d.width, this._dynOutline_c2d.height);

		// Tools.
		// Pencil.
		this._pencil = new Pencil();
		this._pencil.init(this);
		// Brush.
		this._brush = new Brush();
		this._brush.init(this);
		// Spray.
		this._spray = new Spray();
		this._spray.init(this);
		// Stamp.
		this._stamp = new Stamp();
		//this._stamp.init(this);
		this._stamp.init(common.game.scene.gameLayer);
		//InkPot
		this._inkPot = new InkPot();
		this._inkPot.init(this);
		// Eraser.
		this._eraser = new Eraser();
		this._eraser.init(this, common.game.canvas.fillLayer);

		// Layer.
		this._layerSprite = new PIXI.Sprite(PIXI.Texture.fromCanvas(this._canvas2d));
		this._layerSprite.anchor = new PIXI.Point(0.5, 0.5);
		this._layerSprite.interactive = true;
		this._layerSprite.mousedown = this._layerSprite.touchstart = this[_onDrawLayer_MouseDown].bind(this);
		this._layerSprite.mousemove = this._layerSprite.touchmove = this[_onDrawLayer_MouseMove].bind(this);
		this._layerSprite.mouseup = this._layerSprite.touchend = this[_onDrawLayer_MouseUp].bind(this);
		this._layerSprite.mouseupoutside = this._layerSprite.touchendoutside = this[_onDrawLayer_MouseOut].bind(this);
		this.addChild(this._layerSprite);

		// Default tool.
		this[_onDrawMode_Changed](commonGame.TOOL.PENCIL);

		// Listen to HUD controls.
		common.game.hud.signals.onDrawModeChanged.add(this[_onDrawMode_Changed], this);
		common.game.scene.palettePanel.signals.onColourChanged.add(this[_onColour_Changed], this);
	}

	setEnabled(isEnabled) {
		this._layerSprite.interactive = isEnabled;
	}

	update() {
		// Vfx.
		this._drawPS.update(P3.Ticker.dt);

		if (this._drawingTool !== this._spray &&
			this._drawPS.emit &&
			this._lastInputLocalPosition.x === this._updateInputLocalPosition.x &&
			this._lastInputLocalPosition.y === this._updateInputLocalPosition.y)
			this._drawPS.emit = false;

		this._updateInputLocalPosition.x = this._lastInputLocalPosition.x;
		this._updateInputLocalPosition.y = this._lastInputLocalPosition.y;
	}

	setLayerData(data, dynOutlineData) {
		this._context.putImageData(data, 0, 0);
		this._dynOutlineLayerData = dynOutlineData;
		this._dynOutlineContext.putImageData(dynOutlineData, 0, 0);
		this._layerSprite.texture.update();
	}

	clear() {
		this._dynOutlineContext.clearRect(0, 0, this._canvas2d.width, this._canvas2d.height);
		this._dynOutlineLayerData = this._dynOutlineContext.getImageData(0, 0, this._dynOutline_c2d.width, this._dynOutline_c2d.height);

		this._context.clearRect(0, 0, this._canvas2d.width, this._canvas2d.height);

		this._layerSprite.texture.update();
	}

	onContext_Changed() {
		// Tutorial.
		common.game.scene.addTutorialEvent(commonGame.TUT_EVENT.CANVAS_DRAWN);

		this._layerSprite.texture.update();
	}

	[_onDrawLayer_MouseDown](mouseData) {
		//console.log( "mousedown" );

		// Undo manager.
		common.game.undoManager.save();

		// Use canvas' left-top corner as origin.
		const localCoordsPosition = mouseData.data.getLocalPosition(this._layerSprite);
		const ctxCoordPosition = new PIXI.Point();
		ctxCoordPosition.x = Math.floor(localCoordsPosition.x + common.game.canvas.WIDTH * 0.5);
		ctxCoordPosition.y = Math.floor(localCoordsPosition.y + common.game.canvas.HEIGHT * 0.5);

		this._lastInputLocalPosition = new PIXI.Point(ctxCoordPosition.x, ctxCoordPosition.y);
		this._state = this.STATE.DRAWING;

		this._drawingTool.onDrawBegan(ctxCoordPosition);

		// Vfx.
		if (this._drawingTool === this._stamp ||
			this._drawingTool === this._spray) {
			this._drawPS.spawnPos = new PIXI.Point(localCoordsPosition.x, localCoordsPosition.y);
			this._drawPS.emit = true;
		}
	}

	[_onDrawLayer_MouseMove](mouseData) {
		if (this._state !== this.STATE.DRAWING) return;

		// Use canvas' left-top corner as origin.
		var localCoordsPosition = mouseData.data.getLocalPosition(this._layerSprite);
		var ctxCoordPosition = new PIXI.Point();
		ctxCoordPosition.x = Math.floor(localCoordsPosition.x + common.game.canvas.WIDTH * 0.5);
		ctxCoordPosition.y = Math.floor(localCoordsPosition.y + common.game.canvas.HEIGHT * 0.5);

		this._drawingTool.onDrawMoved(this._lastInputLocalPosition, ctxCoordPosition);

		this._lastInputLocalPosition.x = ctxCoordPosition.x;
		this._lastInputLocalPosition.y = ctxCoordPosition.y;

		// Vfx.
		if (this._drawingTool !== this._stamp) {
			if (!this._drawPS.emit) {
				this._drawPS.resetPositionTracking();
				this._drawPS.spawnPos = new PIXI.Point(localCoordsPosition.x, localCoordsPosition.y);
				this._drawPS.emit = true;
			} else
				this._drawPS.updateSpawnPos(localCoordsPosition.x, localCoordsPosition.y);
		}
	}

	[_onDrawLayer_MouseUp](mouseData) {
		if (this._state === this.STATE.DRAWING) {
			this._drawingTool.onDrawEnded();

			this._state = this.STATE.IDLE;

			// Vfx.
			if (this._drawingTool !== this._stamp)
				this._drawPS.emit = false;
		}
	}

	[_onDrawLayer_MouseOut](mouseData) {
		if (this._state === this.STATE.DRAWING) {
			this._drawingTool.onDrawEnded();
			this._state = this.STATE.IDLE;
		}

		// Vfx.
		this._drawPS.emit = false;
	}

	[_onThickness_Changed](selection) {
		this._thickness = this.BASE_LINE_THICKNESS * (selection + 1);
	}

	[_onColour_Changed](selection) {
		if (this._drawingTool === this._pencil ||
			this._drawingTool === this._brush ||
			this._drawingTool === this._spray ||
			this._drawingTool === this._inkPot) {
			let rgbColour = {r: 255, g: 255, b: 255};

			if (this._drawingTool.paletteItem.indexOf('#') === 0)
				rgbColour = common.hexToRgb(this._drawingTool.paletteItem);

			this._drawPS.startColor = [rgbColour.r, rgbColour.g, rgbColour.b];
			this._drawPS.endColor = this._drawPS.startColor;
		}
	}

	[_onDrawMode_Changed](selection) {
		let rgbColour = {r: 255, g: 255, b: 255};

		switch (selection) {
			case commonGame.TOOL.PENCIL: {
				this._drawingTool = this._pencil;

				// Vfx.
				if (this._drawingTool.paletteItem.indexOf('#') === 0)
					rgbColour = common.hexToRgb(this._drawingTool.paletteItem);
				this._drawPS = new PIXI.particles.Emitter(this, [P3.assets.texture('particle_draw')], P3.assets.json('pencil_ps'));
				this._drawPS.emitterLifetime = -1;
				break;
			}

			case commonGame.TOOL.BRUSH: {
				this._drawingTool = this._brush;

				// Vfx.
				if (this._drawingTool.paletteItem.indexOf('#') === 0)
					rgbColour = common.hexToRgb(this._drawingTool.paletteItem);
				this._drawPS = new PIXI.particles.Emitter(this, [P3.assets.texture('particle_paintbrush')], P3.assets.json('brush_ps'));
				this._drawPS.emitterLifetime = -1;
				break;
			}

			case commonGame.TOOL.SPRAY: {
				this._drawingTool = this._spray;

				// Vfx.
				if (this._drawingTool.paletteItem.indexOf('#') === 0)
					rgbColour = common.hexToRgb(this._drawingTool.paletteItem);
				this._drawPS = new PIXI.particles.Emitter(this, [P3.assets.texture('particle_spray')], P3.assets.json('spray_ps'));
				this._drawPS.emitterLifetime = -1;
				break;
			}

			case commonGame.TOOL.STAMP: {
				this._drawingTool = this._stamp;

				// Vfx.
				rgbColour = common.hexToRgb('#ffe400');
				this._drawPS = new PIXI.particles.Emitter(this, [P3.assets.texture('particle_stamp')], P3.assets.json('stamp_ps'));
				this._drawPS.emitterLifetime = 0.1;
				break;
			}

			case commonGame.TOOL.INK_POT: {
				this._drawingTool = this._inkPot;

				// Vfx.
				if (this._drawingTool.paletteItem.indexOf('#') === 0)
					rgbColour = common.hexToRgb(this._drawingTool.paletteItem);
				this._drawPS = new PIXI.particles.Emitter(this, [P3.assets.texture('particle_ink_splatter')], P3.assets.json('ink_splatter_ps'));
				this._drawPS.emitterLifetime = -1;
				break;
			}

			case commonGame.TOOL.ERASER: {
				this._drawingTool = this._eraser;

				// Vfx.
				rgbColour = common.hexToRgb('#fd93bd');
				this._drawPS = new PIXI.particles.Emitter(this, [P3.assets.texture('particle_erase')], P3.assets.json('eraser_ps'));
				this._drawPS.emitterLifetime = -1;
				break;
			}
		}

		this._drawPS.startColor = [rgbColour.r, rgbColour.g, rgbColour.b];
		this._drawPS.endColor = this._drawPS.startColor;
		this._drawPS.emit = false;
	}

	get context() {
		return this._context;
	}

	get dynOutlineContext() {
		return this._dynOutlineContext;
	}

	get state() {
		return this._state;
	}

	get drawingTool() {
		return this._drawingTool;
	}

	get dynOutlineData() {
		return this._dynOutlineLayerData;
	}

	set dynOutlineData(value) {
		return this._dynOutlineLayerData = value;
	}
}
